/**
 * Layout Optimizer for Dynamic Pack Display
 * Determines optimal card layouts based on card count and screen dimensions
 * From Phase 3 feature specification requirements
 */

export class LayoutOptimizer {
    static getOptimalLayout(cardCount, screenWidth, screenHeight) {
        const layouts = {
            1: { type: 'single-row', rows: 1, cols: 1, cardSize: 'extra-large' },
            2: { type: 'single-row', rows: 1, cols: 2, cardSize: 'large' },
            3: { type: 'single-row', rows: 1, cols: 3, cardSize: 'medium' },
            4: { type: 'two-rows', rows: 2, cols: 2, cardSize: 'medium' },
            5: { type: 'two-rows', rows: 2, cols: 3, cardSize: 'medium' },  // Current legacy layout
            6: { type: 'two-rows', rows: 2, cols: 3, cardSize: 'medium' },
            7: { type: 'three-rows', rows: 3, cols: 3, cardSize: 'small' },
            8: { type: 'three-rows', rows: 3, cols: 3, cardSize: 'small' },
            9: { type: 'three-rows', rows: 3, cols: 3, cardSize: 'small' },
            10: { type: 'grid', rows: 4, cols: 3, cardSize: 'small' },
            11: { type: 'grid', rows: 4, cols: 3, cardSize: 'small' },
            12: { type: 'grid', rows: 4, cols: 3, cardSize: 'small' },
        };
        
        let layout = layouts[cardCount] || layouts[Math.min(cardCount, 12)];
        
        // Adjust for screen size
        if (screenWidth < 768) {
            layout = this.optimizeForMobile(cardCount);
        } else if (screenWidth < 1024) {
            layout = this.optimizeForTablet(cardCount, layout);
        }
        
        // Handle very large card counts
        if (cardCount > 12) {
            layout = this.optimizeForLargeCounts(cardCount);
        }
        
        return layout;
    }
    
    static optimizeForMobile(cardCount) {
        // Mobile: prefer vertical layouts for better touch interaction
        if (cardCount <= 2) {
            return { type: 'single-column', rows: cardCount, cols: 1, cardSize: 'large' };
        } else if (cardCount <= 4) {
            return { type: 'two-columns', rows: Math.ceil(cardCount / 2), cols: 2, cardSize: 'medium' };
        } else if (cardCount <= 6) {
            return { type: 'two-columns', rows: 3, cols: 2, cardSize: 'medium' };
        } else {
            return { type: 'two-columns', rows: Math.ceil(cardCount / 2), cols: 2, cardSize: 'small' };
        }
    }
    
    static optimizeForTablet(cardCount, defaultLayout) {
        // Tablet: balance between mobile and desktop
        if (cardCount <= 6) {
            return { ...defaultLayout, cardSize: 'medium' };
        } else if (cardCount <= 9) {
            return { type: 'three-rows', rows: 3, cols: 3, cardSize: 'small' };
        } else {
            return { type: 'grid', rows: Math.ceil(cardCount / 3), cols: 3, cardSize: 'small' };
        }
    }
    
    static optimizeForLargeCounts(cardCount) {
        // Handle packs with many cards (>12)
        const cols = Math.min(5, Math.ceil(Math.sqrt(cardCount)));
        const rows = Math.ceil(cardCount / cols);
        
        return {
            type: 'large-grid',
            rows: rows,
            cols: cols,
            cardSize: 'tiny'
        };
    }
    
    static getCardDimensions(cardSize) {
        const dimensions = {
            'extra-large': { width: 240, height: 320 },
            'large': { width: 180, height: 240 },
            'medium': { width: 140, height: 180 },
            'small': { width: 110, height: 140 },
            'tiny': { width: 90, height: 120 }
        };
        
        return dimensions[cardSize] || dimensions.medium;
    }
}